import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import AdhesionFormDedicated from '../components/adhesion/AdhesionFormDedicated';

export default function AdhesionCOOPEMIEL() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [membershipStatus, setMembershipStatus] = useState({ hasAdhesion: false, isPending: false });
  const [loading, setLoading] = useState(true);

  // Récupérer l'année depuis l'URL
  const anneeParam = searchParams.get('annee');
  const annee = anneeParam ? parseInt(anneeParam) : null;

  useEffect(() => {
    // Rediriger vers la page d'adhésion si pas d'année sélectionnée
    if (!annee) {
      navigate('/adhesion');
      return;
    }
  }, [annee, navigate]);

  useEffect(() => {
    const checkMembership = async () => {
      if (user && annee) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/adhesions/my-adhesions`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Vérifier uniquement pour l'année demandée
          const coopemielAdhesion = response.data.find(
            adhesion => adhesion.organisme === 'COOPEMIEL' 
              && adhesion.status !== 'refusée' 
              && adhesion.annee === annee
          );
          
          if (coopemielAdhesion) {
            setMembershipStatus({
              hasAdhesion: true,
              isPending: ['en_attente', 'paiement_demande'].includes(coopemielAdhesion.status)
            });
          }
        } catch (error) {
          console.error('Erreur lors de la vérification de l\'adhésion:', error);
        }
      }
      setLoading(false);
    };

    checkMembership();
  }, [user, annee]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-slate-600">Chargement...</div>
      </div>
    );
  }

  if (membershipStatus.hasAdhesion) {
    return (
      <div className="min-h-screen py-12 flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center">
              {membershipStatus.isPending ? (
                <>
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-honey-100 mb-4">
                    <svg className="h-10 w-10 text-honey-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    Adhésion en cours de validation
                  </h2>
                  <p className="text-slate-600 mb-6">
                    Votre demande d'adhésion à la COOPEMIEL est en cours de traitement. Vous serez notifié une fois qu'elle sera validée.
                  </p>
                </>
              ) : (
                <>
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                    <svg className="h-10 w-10 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">
                    Vous êtes déjà adhérent
                  </h2>
                  <p className="text-slate-600 mb-6">
                    Vous avez déjà une adhésion active à la COOPEMIEL.
                  </p>
                </>
              )}
              <div className="space-y-3">
                <Link
                  to="/mes-adhesions"
                  className="block w-full px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-honey-500 hover:bg-honey-600"
                >
                  Retour aux adhésions
                </Link>
                <Link
                  to="/adhesion"
                  className="block w-full px-6 py-3 border border-slate-300 text-base font-medium rounded-lg text-slate-700 bg-white hover:bg-slate-50"
                >
                  Voir les autres groupements
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isNextYear = annee > new Date().getFullYear();

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-semibold text-slate-900 sm:text-4xl">
            Adhésion à la COOPEMIEL - {annee}
          </h1>
          <p className="mt-2 text-lg text-slate-600">
            Association de la Maison de l'Apiculture de l'Île de la Réunion
          </p>
          <p className="mt-4 text-base text-slate-500">
            Complétez le formulaire ci-dessous pour soumettre votre demande d'adhésion
          </p>
        </div>

        {/* Encart informatif pour les adhésions N+1 */}
        {isNextYear && (
          <div className="max-w-4xl mx-auto mb-8 rounded-lg bg-honey-50 border border-honey-200 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-honey-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-honey-800">
                  Adhésion pour l'année {annee}
                </h3>
                <p className="mt-1 text-sm text-honey-700">
                  Vous créez une adhésion pour l'année <strong>{annee}</strong>. Les services associés à votre adhésion 
                  (accès aux actualités, facilités, événements...) ne seront actifs qu'à partir du <strong>1er janvier {annee}</strong>.
                </p>
              </div>
            </div>
          </div>
        )}

        <AdhesionFormDedicated organisme="COOPEMIEL" annee={annee} />
      </div>
    </div>
  );
}
